import { useAbility } from '@casl/react';
import { Layers } from '@mui/icons-material';
import Bolt from '@mui/icons-material/Bolt';
import CloudIcon from '@mui/icons-material/Cloud';
import OpacityIcon from '@mui/icons-material/Opacity';
import PlaceIcon from '@mui/icons-material/Place';
import SensorIcon from '@mui/icons-material/Sensors';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import {
  alpha,
  Avatar,
  Box,
  Checkbox,
  Fab,
  FormControlLabel,
  FormGroup,
  Paper,
  Popover,
  Typography,
  useTheme,
} from '@mui/material';
import { blue, purple, yellow } from '@mui/material/colors';
import { useState } from 'react';
import { ACTIONS } from 'src/auth/utils/defineAbilityFor';
import { AbilityContext } from 'src/context/canContext';
import { useDashboard } from 'src/context/dashboardContext';
import {
  ALL_UTILITY_SELECTION_ID,
  isAllUtilitySelection,
} from 'src/utils/selection';
import { UtilityType } from 'switch-shared/modules/crud/utility/utility.enums';

interface MapLayersControlProps {
  showMeters: boolean;
  showGateways: boolean;
  showSensors: boolean;
  showApplications: boolean;
  showElectricityMeters?: boolean;
  showWaterMeters?: boolean;
  onToggleMeters: () => void;
  onToggleGateways: () => void;
  onToggleSensors: () => void;
  onToggleApplications: () => void;
  onToggleElectricityMeters?: () => void;
  onToggleWaterMeters?: () => void;
}

const MapLayersControl = ({
  showMeters,
  showElectricityMeters,
  showWaterMeters,
  showGateways,
  showSensors,
  showApplications,
  onToggleMeters,
  onToggleElectricityMeters,
  onToggleWaterMeters,
  onToggleGateways,
  onToggleSensors,
  onToggleApplications,
}: MapLayersControlProps) => {
  /* Hooks & Use Effects */
  const theme = useTheme();
  const ability = useAbility(AbilityContext);
  const { utility } = useDashboard();

  /* States */
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const isShowingAllUtilities = isAllUtilitySelection(utility);

  /* Functions & Functional Components */
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const getMeterIcon = () => {
    switch (utility) {
      case UtilityType.ELECTRICITY:
        return (
          <Avatar
            sx={{
              bgcolor: yellow[100],
              width: 24,
              height: 24,
            }}
          >
            <Bolt style={{ color: yellow[800] }} fontSize='small' />
          </Avatar>
        );
      case UtilityType.WATER:
        return (
          <Avatar
            sx={{
              bgcolor: blue[50],
              width: 24,
              height: 24,
            }}
          >
            <OpacityIcon style={{ color: blue[500] }} fontSize='small' />
          </Avatar>
        );
      case UtilityType.GAS:
        return (
          <Avatar
            sx={{
              bgcolor: purple[100],
              width: 24,
              height: 24,
            }}
          >
            <CloudIcon style={{ color: purple[800] }} fontSize='small' />
          </Avatar>
        );
      case ALL_UTILITY_SELECTION_ID:
        return (
          <Avatar
            sx={{
              bgcolor: yellow[100],
              width: 24,
              height: 24,
            }}
          >
            <Layers style={{ color: yellow[800] }} fontSize='small' />
          </Avatar>
        );
      default:
        return (
          <Avatar
            sx={{
              bgcolor: yellow[100],
              width: 24,
              height: 24,
            }}
          >
            <Bolt style={{ color: yellow[800] }} fontSize='small' />
          </Avatar>
        );
    }
  };

  const getMeterLabel = () => {
    switch (utility) {
      case UtilityType.ELECTRICITY:
        return 'Electricity Meters';
      case UtilityType.WATER:
        return 'Water Meters';
      case UtilityType.GAS:
        return 'Gas Meters';
      case ALL_UTILITY_SELECTION_ID:
        return 'All Meters';
      default:
        return 'Meters';
    }
  };

  return (
    <>
      <Fab
        size='small'
        onClick={handleClick}
        sx={{
          position: 'absolute',
          bottom: 30,
          color: theme.tokens.brandColors.charcoal,
          background: theme.tokens.colors.secondary,
          right: 16,
          zIndex: 1000,
          boxShadow: theme.tokens.shadows.lg,
        }}
      >
        <Layers />
      </Fab>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock={true}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Paper
          sx={{
            p: 2,
            minWidth: 200,
          }}
        >
          <Typography
            variant='subtitle2'
            sx={{
              mb: 1,
              fontWeight: 600,
              color: theme.tokens.colors.foreground,
            }}
          >
            Map Layers
          </Typography>
          <FormGroup>
            {ability.can(ACTIONS.READ, 'Meters') && (
              <>
                {!isShowingAllUtilities && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showMeters}
                        onChange={onToggleMeters}
                        size='small'
                      />
                    }
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {getMeterIcon()}
                        <Typography variant='body2'>
                          {getMeterLabel()}
                        </Typography>
                      </Box>
                    }
                  />
                )}
                {isShowingAllUtilities && (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showElectricityMeters ?? false}
                          onChange={onToggleElectricityMeters}
                          size='small'
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: yellow[100],
                              width: 24,
                              height: 24,
                            }}
                          >
                            <Bolt
                              style={{ color: yellow[800] }}
                              fontSize='small'
                            />
                          </Avatar>
                          <Typography variant='body2'>
                            Electricity Meters
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showWaterMeters ?? false}
                          onChange={onToggleWaterMeters}
                          size='small'
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: blue[50],
                              width: 24,
                              height: 24,
                            }}
                          >
                            <OpacityIcon
                              style={{ color: blue[500] }}
                              fontSize='small'
                            />
                          </Avatar>
                          <Typography variant='body2'>Water Meters</Typography>
                        </Box>
                      }
                    />
                  </>
                )}
              </>
            )}
            {ability.can(ACTIONS.READ, 'Gateways') && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showGateways}
                    onChange={onToggleGateways}
                    size='small'
                  />
                }
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: '#d5ebff',
                        width: 24,
                        height: 24,
                      }}
                    >
                      <SettingsInputAntennaIcon
                        style={{ color: '#269aff' }}
                        fontSize='small'
                      />
                    </Avatar>
                    <Typography variant='body2'>Gateways</Typography>
                  </Box>
                }
              />
            )}
            {ability.can(ACTIONS.READ, 'Sensors') && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showSensors}
                    onChange={onToggleSensors}
                    size='small'
                  />
                }
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: '#def5ea',
                        width: 24,
                        height: 24,
                      }}
                    >
                      <SensorIcon
                        style={{ color: '#046234' }}
                        fontSize='small'
                      />
                    </Avatar>
                    <Typography variant='body2'>Sensors</Typography>
                  </Box>
                }
              />
            )}
            {ability.can(ACTIONS.READ, 'Applications') && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showApplications}
                    onChange={onToggleApplications}
                    size='small'
                  />
                }
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha(theme.tokens.brandColors.orange, 0.15),
                        width: 24,
                        height: 24,
                      }}
                    >
                      <PlaceIcon
                        style={{ color: theme.tokens.brandColors.orange }}
                        fontSize='small'
                      />
                    </Avatar>
                    <Typography variant='body2'>Applications</Typography>
                  </Box>
                }
              />
            )}
          </FormGroup>
        </Paper>
      </Popover>
    </>
  );
};

export default MapLayersControl;
